import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthTokenContext } from '@/auth/KeycloakProvider';
import { ROUTES, getMasterclassCaseRoute } from '@/config/routes';
import { useMasterclass, CaseProgress } from '@/contexts/MasterclassContext';
import { useUpsertCaseStatistics } from '@/hooks/queries/useCaseStatistics';
import { useProfile, useUpdateProfile } from '@/hooks/queries/useProfile';
import { useIsAccreditationUser } from '@/hooks/useIsAccreditationUser';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';
import { captureSentry } from '@/utils/sentryHelper';

import { CaseSelectorConfig } from '../types';

export const useCaseSelector = (config: CaseSelectorConfig, masterclassId?: string) => {
  const navigate = useNavigate();
  const authContext = useAuthTokenContext();
  const { getCaseMetadata, cases } = useCasesConfig({ masterclassId });
  const {
    showIntroVideo,
    setShowIntroVideo,
    showConfirmationVideo,
    setShowConfirmationVideo,
    setSelectedCase,
    setHasSeenIntro,
    showLoadingAnimation,
    setShowLoadingAnimation,
    resetCaseProgress,
    resetAccreditationUserSlidePosition,
  } = useMasterclass();

  // Get userId from Keycloak auth context
  const userId = authContext.tokenStatus === 'valid' ? authContext.userID : null;

  // Note: Progress is now fetched per-case in CaseCard component
  // Each case has its own MasterClassUserSession (keyed by userId + masterclassId + caseId)
  // These values are kept for backward compatibility but are no longer used
  const caseProgress = useMemo<Record<string, CaseProgress>>(() => ({}), []);
  const progressLoading = false;
  const isAccreditationUser = useIsAccreditationUser();

  const [pendingCase, setPendingCase] = useState<string | null>(null);
  const [certificationCase, setCertificationCase] = useState<string | null>(null);

  // Apollo hooks for profile and statistics
  const { data: profile } = useProfile(userId);
  const updateProfile = useUpdateProfile();
  const upsertStatistics = useUpsertCaseStatistics();

  // Check authentication and onboarding status
  useEffect(() => {
    if (authContext.tokenStatus === 'loading') return;

    if (authContext.tokenStatus === 'invalid') {
      navigate(ROUTES.AUTH);
      return;
    }

    // User is authenticated via Keycloak
    const currentUserId = authContext.userID;

    // Track case selector visit for all cases
    (async () => {
      for (const caseItem of cases) {
        try {
          await upsertStatistics.mutateAsync({
            userId: currentUserId,
            caseId: caseItem.id,
            masterclassId: masterclassId || '',
            updates: {
              case_selector_visits: 1,
            },
          });
        } catch (error) {
          console.error(`Error tracking case selector visit for ${caseItem.id}:`, error);
          captureSentry({
            title: 'Failed to track case selector visit',
            error,
            detail: { userId: currentUserId, caseId: caseItem.id },
          });
        }
      }
    })().catch((error) => {
      console.error('Error tracking case selector visit:', error);
    });
  }, [authContext.tokenStatus, userId, navigate, cases]);

  // Check if user has seen onboarding (separate effect)
  useEffect(() => {
    if (config.modals.showIntroOnFirstVisit && profile !== null) {
      const shouldShow = !profile.has_seen_onboarding;
      setShowIntroVideo(shouldShow);
    } else if (!config.modals.showIntroOnFirstVisit) {
      setShowIntroVideo(false);
    }
  }, [profile, config.modals.showIntroOnFirstVisit, setShowIntroVideo]);

  // Reset confirmation modal on component mount to prevent it from showing on page return
  useEffect(() => {
    setShowConfirmationVideo(false);
  }, [setShowConfirmationVideo]);

  const handleIntroClose = () => {
    setShowIntroVideo(false);
    setHasSeenIntro(true);

    // Mark onboarding as seen in localStorage
    if (userId && authContext.tokenStatus === 'valid') {
      updateProfile.mutate({
        userId,
        updates: { has_seen_onboarding: true },
      });
    }
  };

  const handleCaseSelect = async (caseId: string, hasProgress: boolean = false) => {
    trackEvent('Case Selection', 'Select Case', caseId, null);
    if (userId) {
      // Track case click
      upsertStatistics
        .mutateAsync({
          userId,
          caseId,
          masterclassId: masterclassId || '',
          updates: {
            case_clicks: 1, // Increment case clicks
          },
        })
        .catch((error) => {
          console.error('Error tracking case click:', error);
          captureSentry({
            title: 'Failed to track case click',
            error,
            detail: { userId, caseId },
          });
        });
    }

    // Get case metadata for intro video
    const metadata = getCaseMetadata(caseId);
    if (!metadata) return;

    // Handle reset based on user type
    if (caseProgress[caseId]?.completed && isAccreditationUser) {
      // Accreditation users: only reset slide position, keep answers
      await resetAccreditationUserSlidePosition(masterclassId || '', caseId);
    }

    // Show confirmation modal only if:
    // 1. Configuration enables it AND
    // 2. User has no existing progress on this case (first time)
    // If user has progress, skip the intro and go straight to the case
    if (config.modals.showConfirmationBeforeStart && !hasProgress) {
      // Set pending case - intro video URL will be derived from case details in CaseSelector
      setPendingCase(caseId);
      setShowConfirmationVideo(true);
    } else {
      startCase(caseId);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationVideo(false);
    setPendingCase(null);
  };

  const handleConfirmationStart = () => {
    setShowConfirmationVideo(false);

    // Mark onboarding as seen when user starts their first case
    if (userId && pendingCase && authContext.tokenStatus === 'valid') {
      updateProfile.mutate({
        userId,
        updates: { has_seen_onboarding: true },
      });

      startCase(pendingCase);
    }
  };

  const startCase = (caseId: string) => {
    setShowLoadingAnimation(true);

    setTimeout(() => {
      setSelectedCase(caseId);
      setShowLoadingAnimation(false);
      // Navigate to the case detail page using database ID
      if (masterclassId) {
        navigate(getMasterclassCaseRoute(masterclassId, caseId));
      } else {
        // Fallback if no masterclassId (shouldn't happen in normal flow)
        navigate(`/case/${caseId}`);
      }
    }, config.navigation.loadingDuration);
  };

  const handleSignOut = () => {
    authContext.logout();
  };

  return {
    userId,
    pendingCase,
    certificationCase,
    setCertificationCase,
    showIntroVideo,
    showConfirmationVideo,
    isLoading: showLoadingAnimation,
    caseProgress,
    handleIntroClose,
    handleCaseSelect,
    handleConfirmationClose,
    handleConfirmationStart,
    handleSignOut,
    caseProgressLoading: progressLoading,
  };
};
